import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import IconifyIcon from 'components/base/IconifyIcon';
import { PLAN_CONFIG, PlanType } from 'data/plan-config';
import { weddingPaths } from 'routes/paths';

interface SidenavPremiumBadgeProps {
  plan: 'basic' | 'pro';
  collapsed?: boolean;
}

const SidenavPremiumBadge = ({ plan, collapsed = false }: SidenavPremiumBadgeProps) => {
  const { t } = useTranslation();
  const planName = PLAN_CONFIG[plan as PlanType]?.displayName ?? plan;

  // Use a subtle golden/warning scheme for the premium feel
  const bgColor = plan === 'pro' ? 'warning.lighter' : 'info.lighter';
  const iconColor = plan === 'pro' ? 'warning.main' : 'info.main';
  const borderColor = plan === 'pro' ? 'warning.light' : 'info.light';
  const iconName =
    plan === 'pro' ? 'solar:crown-star-bold-duotone' : 'solar:star-fall-bold-duotone';

  if (collapsed) {
    return (
      <Tooltip title={`${planName}`} placement="right" arrow>
        <Box
          component={Link}
          to={weddingPaths.coupleBilling}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: bgColor,
            color: iconColor,
            transition: 'all 0.2s',
            cursor: 'pointer',
            textDecoration: 'none',
            boxShadow: `0 0 10px 0px rgba(0,0,0,0.05)`,
            '&:hover': { opacity: 0.85 },
          }}
        >
          <IconifyIcon icon={iconName} fontSize={20} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      component={Link}
      to={weddingPaths.coupleBilling}
      sx={{
        mx: 2,
        mb: 3,
        p: 2,
        borderRadius: 3,
        bgcolor: bgColor,
        border: '1px solid',
        borderColor: borderColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 0 12px 0px rgba(0,0,0,0.03)`,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { opacity: 0.85 },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <IconifyIcon icon={iconName} fontSize={22} sx={{ color: iconColor }} />
        <Stack direction="column" alignItems="flex-start">
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            {planName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              lineHeight: 1.2,
            }}
          >
            {t('nav.premium.current_plan')}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SidenavPremiumBadge;
